(() => {
  /* global Swiper */
  const COMPONENT_NAME = "model-blocks";
  const SLIDE_CLASS = "swiper-slide";
  const MOBILE_QUERY = "(max-width: 767px)";
  const DEFAULT_GAP = 16;
  const SWIPER_POLL_MS = 50;
  const SWIPER_POLL_LIMIT = 40;

  if (customElements.get(COMPONENT_NAME)) return;

  class ModelBlocks extends HTMLElement {
    #abortController = null;
    #cards = [];
    #labels = [];
    #activeLabel = null;
    #showAll = false;
    #nav = null;
    #navRow = null;
    #pinnedCard = null;
    #carousel = null;
    #swiper = null;
    #mobileQuery = null;
    #initAttempts = 0;
    #initTimer = null;

    connectedCallback() {
      this.#abortController = new AbortController();
      this.#nav = this.querySelector("[data-model-blocks-nav]");
      this.#navRow = this.querySelector("[data-model-blocks-nav-row]");
      this.#carousel = this.querySelector("[data-model-blocks-carousel]");
      this.#cards = Array.from(this.querySelectorAll("[data-model-card]"));
      this.#labels = this.#collectLabels();
      /* One card per size: every size renders at once behind an "All" tab, so
         the starting label is null (no filter) rather than the first size. */
      this.#showAll =
        this.dataset.showAll === "true" && this.#labels.length > 1;
      this.#renderNav();
      this.#wireTabPanels();
      this.#bindDesignMode();
      this.#selectLabel(this.#showAll ? null : (this.#labels[0] ?? null), {
        focus: false,
      });
      this.classList.add("model-blocks--ready");
      this.#bindBreakpoint();
      this.#syncCarousel();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      this.#destroySwiper();
      this.#mobileQuery = null;
      this.#cards = [];
      this.#labels = [];
      this.#activeLabel = null;
      this.#showAll = false;
      this.#nav = null;
      this.#navRow = null;
      this.#carousel = null;
      this.#pinnedCard = null;
      this.classList.remove("model-blocks--ready");
      this.classList.remove("model-blocks--show-all");
    }

    /* Swiper only runs at the mobile breakpoint — from 768px the cards are a
       centred row sized by CSS, and Swiper's inline slide widths would fight
       it. So the instance is built and torn down as the breakpoint flips. */
    #bindBreakpoint() {
      if (!window.matchMedia) return;

      this.#mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.#mobileQuery.addEventListener(
        "change",
        () => {
          this.#syncCarousel();
        },
        { signal: this.#abortController?.signal },
      );
    }

    #isMobile() {
      return this.#mobileQuery ? this.#mobileQuery.matches : false;
    }

    #syncCarousel() {
      if (this.dataset.carousel !== "true" || !this.#carousel) return;

      if (this.#isMobile()) {
        this.#mountSwiper();
      } else {
        this.#destroySwiper();
      }
    }

    #mountSwiper() {
      if (this.#swiper || !this.isConnected) return;

      /* swiper-bundle.min.js is deferred, so the element can upgrade first. */
      if (typeof Swiper === "undefined") {
        if (this.#initAttempts > SWIPER_POLL_LIMIT) return;

        this.#initAttempts += 1;
        this.#initTimer = window.setTimeout(() => {
          this.#initTimer = null;
          this.#mountSwiper();
        }, SWIPER_POLL_MS);
        return;
      }

      this.#initAttempts = 0;
      this.#prepareSlides();

      const list = this.querySelector("[data-model-blocks-list]");
      if (!list || list.children.length === 0) return;

      this.#swiper = new Swiper(this.#carousel, {
        slidesPerView: "auto",
        spaceBetween: this.#getGap(),
        grabCursor: true,
        watchOverflow: true,
        roundLengths: true,
        resistanceRatio: 0.85,
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
      });

      this.classList.add("model-blocks--carousel-ready");
    }

    #destroySwiper() {
      if (this.#initTimer !== null) {
        window.clearTimeout(this.#initTimer);
        this.#initTimer = null;
      }

      this.#initAttempts = 0;
      this.#swiper?.destroy(true, true);
      this.#swiper = null;
      this.classList.remove("model-blocks--carousel-ready");
    }

    /* Size filtering hides slides with the `hidden` attribute, which Swiper's
       measuring pass does not account for — rebuilding is cheaper to reason
       about than patching the track in place. */
    #refreshCarousel() {
      if (!this.#swiper) return;

      this.#destroySwiper();
      this.#mountSwiper();
    }

    #prepareSlides() {
      const list = this.querySelector("[data-model-blocks-list]");
      if (!list) return;

      for (const child of list.children) {
        child.classList.add(SLIDE_CLASS);
      }
    }

    #getGap() {
      const fromData = Number.parseInt(this.dataset.gap ?? "", 10);
      if (!Number.isNaN(fromData)) return fromData;

      return DEFAULT_GAP;
    }

    #sectionId() {
      return this.dataset.sectionId ?? "section";
    }

    #collectLabels() {
      const labels = [];

      for (const card of this.#cards) {
        const label = (card.dataset.sizeLabel ?? "").trim();

        if (label !== "" && !labels.includes(label)) {
          labels.push(label);
        }
      }

      return labels;
    }

    #cardWrapper(card) {
      return card.closest(".shopify-block") ?? card;
    }

    #bindDesignMode() {
      if (!window.Shopify?.designMode) return;

      const signal = this.#abortController?.signal;

      document.addEventListener(
        "shopify:block:select",
        (event) => {
          if (
            !(event.target instanceof Element) ||
            !this.contains(event.target)
          ) {
            return;
          }

          const card = this.#findCardFromTarget(event.target);
          if (!card) return;

          const label = (card.dataset.sizeLabel ?? "").trim();

          if (label !== "" && this.#labels.includes(label)) {
            this.#pinnedCard = null;
            this.#selectLabel(label, { focus: false });
            return;
          }

          // Blank-label cards stay outside size nav — pin them visible.
          this.#pinnedCard = card;
          this.#filterCards();
        },
        { signal },
      );

      document.addEventListener(
        "shopify:block:deselect",
        (event) => {
          if (
            !(event.target instanceof Element) ||
            !this.contains(event.target)
          ) {
            return;
          }

          this.#pinnedCard = null;
          this.#filterCards();
        },
        { signal },
      );
    }

    #findCardFromTarget(target) {
      return (
        target.closest("[data-model-card]") ??
        target.querySelector("[data-model-card]")
      );
    }

    #renderNav() {
      if (!this.#nav) return;

      this.#nav.replaceChildren();

      if (this.#labels.length < 2) {
        if (this.#navRow) this.#navRow.hidden = true;
        this.#nav.removeAttribute("role");
        this.#nav.removeAttribute("aria-label");
        return;
      }

      if (this.#navRow) this.#navRow.hidden = false;
      this.#nav.setAttribute("role", "tablist");
      this.#nav.setAttribute(
        "aria-label",
        this.dataset.navLabel ?? "Model sizes",
      );

      const sectionId = this.#sectionId();

      if (this.#showAll) {
        this.#nav.append(
          this.#navButton({
            label: null,
            text: this.dataset.navAllLabel || "All",
            id: `ModelBlocksTab-${sectionId}-all`,
          }),
        );
      }

      for (const [index, label] of this.#labels.entries()) {
        this.#nav.append(
          this.#navButton({
            label,
            text: label,
            id: `ModelBlocksTab-${sectionId}-${index}`,
          }),
        );
      }
    }

    /* `label: null` builds the "All" tab — the no-filter state. It carries
       data-size-all instead of data-size-label so the two never collide with a
       size literally named "All". */
    #navButton({ label, text, id }) {
      const signal = this.#abortController?.signal;
      const button = document.createElement("button");

      button.type = "button";
      button.className = "model-blocks__nav-button font-body-lg--bold";
      button.textContent = text;
      button.setAttribute("role", "tab");
      button.id = id;

      if (label === null) {
        button.dataset.sizeAll = "";
      } else {
        button.dataset.sizeLabel = label;
      }

      button.addEventListener(
        "click",
        () => {
          this.#pinnedCard = null;
          this.#selectLabel(label, { focus: false });
        },
        { signal },
      );

      button.addEventListener(
        "keydown",
        (event) => {
          this.#onNavKeydown(event, this.#navLabels().indexOf(label));
        },
        { signal },
      );

      return button;
    }

    /* Tab order, with null in front for the "All" tab when it is rendered. */
    #navLabels() {
      return this.#showAll ? [null, ...this.#labels] : this.#labels;
    }

    #buttonLabel(button) {
      if ("sizeAll" in button.dataset) return null;

      return button.dataset.sizeLabel ?? null;
    }

    #wireTabPanels() {
      const hasTabs = this.#labels.length > 1;
      const sectionId = this.#sectionId();
      const labelToTabId = new Map();
      const labelToPanelIds = new Map();
      const allPanelIds = [];

      for (const [index, label] of this.#labels.entries()) {
        labelToTabId.set(label, `ModelBlocksTab-${sectionId}-${index}`);
        labelToPanelIds.set(label, []);
      }

      for (const [cardIndex, card] of this.#cards.entries()) {
        const label = (card.dataset.sizeLabel ?? "").trim();
        const panelId = `ModelBlocksPanel-${sectionId}-${cardIndex}`;

        if (!hasTabs || label === "" || !labelToTabId.has(label)) {
          if (card.id === panelId) {
            card.removeAttribute("id");
          }

          card.removeAttribute("role");
          card.removeAttribute("aria-labelledby");
          continue;
        }

        const tabId = labelToTabId.get(label);

        // Put panel id/roles on the card, never the Shopify block wrapper.
        card.id = panelId;
        card.setAttribute("role", "tabpanel");
        card.setAttribute("aria-labelledby", tabId);
        labelToPanelIds.get(label).push(panelId);
        allPanelIds.push(panelId);
      }

      if (!this.#nav || !hasTabs) return;

      for (const button of this.#nav.querySelectorAll('[role="tab"]')) {
        const buttonLabel = this.#buttonLabel(button);
        // The "All" tab shows every size, so it controls every panel.
        const panelIds =
          buttonLabel === null
            ? allPanelIds
            : (labelToPanelIds.get(buttonLabel) ?? []);

        if (panelIds.length > 0) {
          button.setAttribute("aria-controls", panelIds.join(" "));
        } else {
          button.removeAttribute("aria-controls");
        }
      }
    }

    #onNavKeydown(event, index) {
      const key = event.key;
      const labels = this.#navLabels();
      let nextIndex = null;

      if (index < 0) return;

      if (key === "ArrowRight" || key === "ArrowDown") {
        nextIndex = (index + 1) % labels.length;
      } else if (key === "ArrowLeft" || key === "ArrowUp") {
        nextIndex = (index - 1 + labels.length) % labels.length;
      } else if (key === "Home") {
        nextIndex = 0;
      } else if (key === "End") {
        nextIndex = labels.length - 1;
      }

      if (nextIndex === null) return;

      event.preventDefault();
      this.#pinnedCard = null;
      this.#selectLabel(labels[nextIndex], { focus: true });
    }

    #selectLabel(label, { focus = false } = {}) {
      this.classList.add("model-blocks--tab-fade");
      this.#activeLabel = label;
      this.classList.toggle(
        "model-blocks--show-all",
        this.#showAll && label === null,
      );
      this.#updateNav(focus);
      this.#filterCards();
      window.requestAnimationFrame(() => {
        this.classList.remove("model-blocks--tab-fade");
      });
    }

    #updateNav(focus) {
      if (!this.#nav) return;

      for (const button of this.#nav.querySelectorAll('[role="tab"]')) {
        const isActive = this.#buttonLabel(button) === this.#activeLabel;

        button.classList.toggle("model-blocks__nav-button--active", isActive);
        button.setAttribute("aria-selected", isActive ? "true" : "false");
        button.tabIndex = isActive ? 0 : -1;

        if (isActive && focus) {
          button.focus();
        }
      }
    }

    #filterCards() {
      const hasSizeFilter = this.#labels.length > 1;
      const hasFilter = hasSizeFilter && this.#activeLabel != null;

      for (const card of this.#cards) {
        const cardLabel = (card.dataset.sizeLabel ?? "").trim();
        const isPinned = this.#pinnedCard === card;
        // Blank tab labels are outside size nav — keep those cards visible.
        const matches =
          !hasFilter ||
          cardLabel === "" ||
          cardLabel === this.#activeLabel ||
          isPinned;
        const wrapper = this.#cardWrapper(card);

        wrapper.hidden = !matches;
        wrapper.setAttribute("aria-hidden", matches ? "false" : "true");
      }

      const list = this.querySelector("[data-model-blocks-list]");

      if (!list) return;

      // App (and other non-card) blocks are not size-scoped — keep them out of
      // the filtered / carousel track while size navigation is active.
      for (const child of list.children) {
        if (child.querySelector("[data-model-card]")) continue;

        child.hidden = hasSizeFilter;
        child.setAttribute("aria-hidden", hasSizeFilter ? "true" : "false");
      }

      list.scrollLeft = 0;
      this.#refreshCarousel();
    }
  }

  customElements.define(COMPONENT_NAME, ModelBlocks);
})();
